import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Loader2, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LiteraturePicker } from "@/components/recovery/literature-picker";
import { Input } from "@/components/ui/input";
import { signOut } from "@/lib/auth-client";
import {
  ApiError,
  deleteAccount,
  getSettings,
  updateProfile,
  updateReminders,
  type FellowshipId,
  type Terminology,
} from "@/lib/api";

const browserTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
};

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [sobriety, setSobriety] = useState("");
  const [fellowships, setFellowships] = useState<FellowshipId[]>([]);
  // The word this account uses for time, resolved server-side from the above.
  const [term, setTerm] = useState<Terminology | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const [remOn, setRemOn] = useState(false);
  const [remTime, setRemTime] = useState("20:00");
  const [tz, setTz] = useState(browserTz());
  const [remindersSaved, setRemindersSaved] = useState(false);
  const [savingReminders, setSavingReminders] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    getSettings()
      .then((s) => {
        setName(s.profile.display_name === "Anonymous" ? "" : s.profile.display_name);
        setSobriety(s.profile.sobriety_start_date ?? "");
        setFellowships(s.profile.fellowships);
        setTerm(s.profile.terminology);
        if (s.reminders) {
          setRemOn(s.reminders.is_active === 1);
          setRemTime(s.reminders.reminder_time);
          setTz(s.reminders.timezone);
        }
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) navigate("/signin");
        else setError("Couldn't load your settings. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  async function saveProfile() {
    setSavingProfile(true);
    setError(null);
    try {
      const saved = await updateProfile({
        display_name: name.trim(),
        sobriety_start_date: sobriety || null,
        fellowships,
      });
      // The word follows the selection, so re-read it rather than guessing.
      setTerm(saved.profile.terminology);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2000);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save. Please try again.");
    } finally {
      setSavingProfile(false);
    }
  }

  async function saveReminders() {
    setSavingReminders(true);
    setError(null);
    try {
      await updateReminders({ reminder_time: remTime, timezone: tz, is_active: remOn });
      setRemindersSaved(true);
      setTimeout(() => setRemindersSaved(false), 2000);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save. Please try again.");
    } finally {
      setSavingReminders(false);
    }
  }

  async function doDelete() {
    setDeleting(true);
    try {
      await deleteAccount();
      await signOut().catch(() => {});
      navigate("/");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't delete your account.");
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }

  return (
    <div className="pb-24">
      <div className="flex items-center gap-3 px-6 pb-2 pt-8">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate("/app")}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
      </div>

      <div className="flex flex-col gap-6 px-6 pt-4">
        {error && <p className="text-sm text-status-red-text">{error}</p>}

        {/* Profile */}
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.05em] text-muted-foreground">
            Profile
          </h2>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="name">
              Display name
            </label>
            <Input
              id="name"
              placeholder="What should we call you?"
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="sobriety">
              {term?.date ?? "Sobriety start date"}
            </label>
            <Input
              id="sobriety"
              type="date"
              max={new Date().toISOString().slice(0, 10)}
              value={sobriety}
              onChange={(e) => setSobriety(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Shown quietly on your dashboard. Leave blank to hide it.
            </p>
          </div>
          <Button onClick={saveProfile} disabled={savingProfile} className="self-start">
            {savingProfile ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
            ) : profileSaved ? (
              <>
                <Check className="h-4 w-4" strokeWidth={2} /> Saved
              </>
            ) : (
              "Save profile"
            )}
          </Button>
        </section>

        {/* Which literature the readings cite (Fellowships PRD §10.1). Always
            available, never a gate, and clearable back to nothing. */}
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.05em] text-muted-foreground">
            Your literature
          </h2>
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            Only so the step readings point at the right book. Pick as many as apply, or none.
          </p>
          <LiteraturePicker value={fellowships} onChange={setFellowships} />
          <p className="text-xs leading-relaxed text-muted-foreground">
            Changing your literature only changes which readings are cited. Nothing you've written
            moves or disappears.
          </p>
          <Button onClick={saveProfile} disabled={savingProfile} className="self-start">
            {savingProfile ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
            ) : profileSaved ? (
              <>
                <Check className="h-4 w-4" strokeWidth={2} /> Saved
              </>
            ) : (
              "Save literature"
            )}
          </Button>
        </section>

        {/* Reminders */}
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.05em] text-muted-foreground">
            Daily reminder
          </h2>
          <Card>
            <CardContent className="flex items-center justify-between gap-3 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-foreground">Remind me to check in</p>
                <p className="text-xs text-muted-foreground">One gentle nudge a day.</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={remOn}
                aria-label="Toggle daily reminder"
                onClick={() => setRemOn((v) => !v)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                  remOn ? "bg-primary" : "bg-border"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                    remOn ? "translate-x-[22px]" : "translate-x-0.5"
                  }`}
                />
              </button>
            </CardContent>
          </Card>
          {remOn && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="remtime">
                Reminder time
              </label>
              <Input
                id="remtime"
                type="time"
                value={remTime}
                onChange={(e) => setRemTime(e.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                In your timezone ({tz.replace(/_/g, " ")}).
              </p>
            </div>
          )}
          <Button onClick={saveReminders} disabled={savingReminders} className="self-start">
            {savingReminders ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
            ) : remindersSaved ? (
              <>
                <Check className="h-4 w-4" strokeWidth={2} /> Saved
              </>
            ) : (
              "Save reminder"
            )}
          </Button>
        </section>

        {/* Account */}
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.05em] text-muted-foreground">
            Account
          </h2>
          <Button
            variant="outline"
            fullWidth
            onClick={async () => {
              await signOut();
              navigate("/");
            }}
          >
            <LogOut className="h-4 w-4" strokeWidth={2} />
            Sign out
          </Button>

          <Card className="border-status-red-bg">
            <CardContent className="flex flex-col gap-3 p-4">
              <div>
                <p className="text-sm font-medium text-foreground">Delete account</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Permanently erases your entries, reflections, step work, and everything
                  else. This can't be undone.
                </p>
              </div>
              {confirmDelete ? (
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    onClick={doDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                    ) : (
                      "Yes, delete everything"
                    )}
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="self-start border-status-red text-status-red-text"
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete my account
                </Button>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
